import { Link } from '@tanstack/react-router';
import type { MouseEvent } from 'react';
import type { TaskView } from '../../lib/client.ts';

const LINK = 'text-brand hover:underline';

/**
 * The record a task is on, linked to where that record opens (TS-1738): a candidate or a
 * project to its page, a job to its project with the job expanded, a deal to its project's
 * Deals tab — the same places the Jobs and Deals pages open them, and a company in the
 * Companies side panel. Contacts have no page, so their name shows without a link.
 */
export function TaskRecordLink({
  task,
  onClick,
}: {
  task: TaskView;
  onClick?: (e: MouseEvent) => void;
}) {
  const common = { className: LINK, 'data-testid': 'task-record', onClick } as const;
  const label = task.entityLabel;
  const projectId = task.entityProjectId;
  switch (task.entityType) {
    case 'candidates':
      return (
        <Link to="/candidates/$id" params={{ id: task.entityId }} {...common}>
          {label}
        </Link>
      );
    case 'projects':
      return (
        <Link to="/projects/$id" params={{ id: task.entityId }} {...common}>
          {label}
        </Link>
      );
    case 'companies':
      return (
        <Link to="/companies" search={{ open: task.entityId }} {...common}>
          {label}
        </Link>
      );
    case 'jobs':
      if (!projectId) break;
      return (
        <Link
          to="/projects/$id"
          params={{ id: projectId }}
          search={{ job: task.entityId }}
          {...common}
        >
          {label}
        </Link>
      );
    case 'deals':
      if (!projectId) break;
      return (
        <Link to="/projects/$id" params={{ id: projectId }} search={{ tab: 'deals' }} {...common}>
          {label}
        </Link>
      );
  }
  return <span data-testid="task-record">{label}</span>;
}
